/**
 * Centralized Education Options
 * 
 * Single source of truth for education-related dropdowns across the application.
 * Used by wizard Education step, admin displays, and profile forms.
 */

// Education levels - ordered from lowest to highest
export const EDUCATION_LEVELS = [
    { value: 'High School / O-Levels', key: 'wizard.education.levels.highSchool', points: 1 },
    { value: 'A-Levels', key: 'wizard.education.levels.aLevels', points: 2 },
    { value: 'Diploma', key: 'wizard.education.levels.diploma', points: 3 },
    { value: "Bachelor's Degree", key: 'wizard.education.levels.bachelors', points: 4 },
    { value: "Master's Degree", key: 'wizard.education.levels.masters', points: 5 },
    { value: 'PhD / Doctorate', key: 'wizard.education.levels.phd', points: 6 },
    { value: 'Professional Certification', key: 'wizard.education.levels.professionalCertification', points: 3 },
    { value: 'Other', key: 'wizard.education.levels.other', points: 1 }
];

// General study fields
export const STUDY_FIELDS = [
    { value: 'Engineering', key: 'wizard.education.fields.engineering' },
    { value: 'Medicine / Healthcare', key: 'wizard.education.fields.medicineHealthcare' },
    { value: 'Business Administration', key: 'wizard.education.fields.business' },
    { value: 'Computer Science / IT', key: 'wizard.education.fields.it' },
    { value: 'Natural Sciences', key: 'wizard.education.fields.naturalSciences' },
    { value: 'Social Sciences', key: 'wizard.education.fields.socialSciences' },
    { value: 'Arts & Humanities', key: 'wizard.education.fields.artsHumanities' },
    { value: 'Law', key: 'wizard.education.fields.law' },
    { value: 'Education', key: 'wizard.education.fields.education' },
    { value: 'Agriculture', key: 'wizard.education.fields.agriculture' },
    { value: 'Other', key: 'wizard.education.fields.other' }
];

// Course of study options (German university classification)
export const COURSE_OF_STUDY_OPTIONS = [
    { value: 'Agricultural and Forest Sciences', key: 'wizard.education.course.agri' },
    { value: 'Humanities and Social Sciences', key: 'wizard.education.course.humanities' },
    { value: 'Engineering Sciences', key: 'wizard.education.course.engineering' },
    { value: 'Art, Music, Design', key: 'wizard.education.course.art' },
    { value: 'Mathematics, Natural Sciences', key: 'wizard.education.course.math' },
    { value: 'Medicine, Health Sciences', key: 'wizard.education.course.medicine' },
    { value: 'Language and Cultural Studies', key: 'wizard.education.course.language' },
    { value: 'Economic Sciences, Law', key: 'wizard.education.course.econ' },
    { value: 'Teaching Degrees', key: 'wizard.education.course.teaching' },
    { value: 'Public Administration', key: 'wizard.education.course.publicAdmin' }
];

// Language proficiency levels
export const LANGUAGE_PROFICIENCY_LEVELS = [
    { value: 'beginner', label: 'Beginner (A1-A2)', key: 'wizard.language.levels.beginner' },
    { value: 'intermediate', label: 'Intermediate (B1-B2)', key: 'wizard.language.levels.intermediate' },
    { value: 'advanced', label: 'Advanced (C1)', key: 'wizard.language.levels.advanced' },
    { value: 'native', label: 'Native/Fluent (C2)', key: 'wizard.language.levels.native' }
];

// German language levels (for Germany-specific immigration)
export const GERMAN_LANGUAGE_LEVELS = [
    { value: 'none', label: 'No German knowledge', key: 'wizard.german.levels.none' },
    { value: 'A1', label: 'A1 - Basic', key: 'wizard.german.levels.a1' },
    { value: 'A2', label: 'A2 - Elementary', key: 'wizard.german.levels.a2' },
    { value: 'B1', label: 'B1 - Intermediate', key: 'wizard.german.levels.b1' },
    { value: 'B2', label: 'B2 - Upper Intermediate', key: 'wizard.german.levels.b2' },
    { value: 'C1', label: 'C1 - Advanced', key: 'wizard.german.levels.c1' },
    { value: 'C2', label: 'C2 - Proficient', key: 'wizard.german.levels.c2' }
];

// Experience level options
export const EXPERIENCE_LEVELS = [
    { value: 'none', label: 'No experience', years: 0 },
    { value: 'less_than_1', label: 'Less than 1 year', years: 0.5 },
    { value: '1_2', label: '1-2 years', years: 1.5 },
    { value: '2_3', label: '2-3 years', years: 2.5 },
    { value: '3_5', label: '3-5 years', years: 4 },
    { value: '5_10', label: '5-10 years', years: 7.5 },
    { value: 'more_than_10', label: 'More than 10 years', years: 12 }
];

// Financial/Budget ranges
export const BUDGET_RANGES = [
    { value: 'under_5000', label: 'Under $5,000 USD', min: 0, max: 5000 },
    { value: '5000_10000', label: '$5,000 - $10,000 USD', min: 5000, max: 10000 },
    { value: '10000_20000', label: '$10,000 - $20,000 USD', min: 10000, max: 20000 },
    { value: '20000_50000', label: '$20,000 - $50,000 USD', min: 20000, max: 50000 },
    { value: 'over_50000', label: 'Over $50,000 USD', min: 50000, max: Infinity }
];

// Currency options
export const CURRENCIES = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'LKR', symbol: 'Rs', name: 'Sri Lankan Rupee' },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' }
];

// Helper functions
export const getEducationLevelByValue = (value) =>
    EDUCATION_LEVELS.find(e => e.value === value);

export const getStudyFieldByValue = (value) =>
    STUDY_FIELDS.find(f => f.value === value);

export const getEducationLevelValues = () =>
    EDUCATION_LEVELS.map(e => e.value);

export const getStudyFieldValues = () =>
    STUDY_FIELDS.map(f => f.value);

export const getCourseOfStudyValues = () =>
    COURSE_OF_STUDY_OPTIONS.map(c => c.value);

export default {
    EDUCATION_LEVELS,
    STUDY_FIELDS,
    COURSE_OF_STUDY_OPTIONS,
    LANGUAGE_PROFICIENCY_LEVELS,
    GERMAN_LANGUAGE_LEVELS,
    EXPERIENCE_LEVELS,
    BUDGET_RANGES,
    CURRENCIES
};
